import { SHELL_RULES } from "../../config/gameplay";
import { clamp } from "../../utils/math";
import type { ProfileProgress } from "../progression/levels";
import type { UpgradeLevels } from "./definitions";

export interface UpgradeEffects {
  readonly maxCores: number;
  readonly overchargedChance: number;
  readonly coreBaseValue: number;
  readonly chargeValue: number;
  readonly shellSpeedMultiplier: number;
  readonly gateBonusDegrees: number;
  readonly gateCount: number;
  readonly extraShellCount: number;
  readonly collectorAmpMultiplier: number;
  readonly criticalChance: number;
  readonly criticalMultiplier: number;
  readonly offlineRewardRate: number;
  readonly offlineMaxSeconds: number;
  readonly offlineRewardCapShare: number;
  readonly polarityEnabled: boolean;
  readonly polarityStackCap: number;
  readonly polarityChargePerStack: number;
  readonly polarityChargeMax: number;
  readonly polarityPrestigeMultiplier: number;
  readonly polarityWindowCount: number;
  readonly polarityWindowHalfDegrees: number;
  readonly shellLensWindowBonus: number;
  readonly shellLensLaneBonusMultiplier: number;
}

export function calculateUpgradeEffects(upgrades: UpgradeLevels, progress: ProfileProgress): UpgradeEffects {
  const profileLevel = Math.max(1, progress.effectiveLevel);
  const prestigeOverchargeBonus = progress.prestigeLevel > 0 ? Math.min(0.075, 0.02 + (progress.prestigeLevel - 1) * 0.0014) : 0;
  const prestigeCollectorMultiplier = progress.prestigeLevel > 0 ? Math.min(1.55, 1.1 + (progress.prestigeLevel - 1) * 0.0115) : 1;

  return {
    maxCores: clamp(2 + upgrades.MaxCores, 2, 50),
    overchargedChance: calculateOverchargedChance(upgrades.OverchargedCore, prestigeOverchargeBonus),
    coreBaseValue: calculateCoreBaseValue(upgrades.CoreValue, profileLevel),
    chargeValue: calculateChargeValue(upgrades.ChargeBonus, profileLevel),
    shellSpeedMultiplier: 1 + upgrades.ShellSpeed * 0.015 + Math.max(0, upgrades.ShellSpeed - 12) * 0.004,
    gateBonusDegrees: clamp(upgrades.GateWidth * 0.7 + Math.max(0, upgrades.GateWidth - 10) * 0.18 + Math.max(0, upgrades.GateWidth - 14) * 0.1, 0, 12.8),
    gateCount: clamp(1 + upgrades.GateCount, 1, 4),
    extraShellCount: clamp(upgrades.ExtraShell, 0, SHELL_RULES.maxShells - SHELL_RULES.startingShells),
    collectorAmpMultiplier: calculateCollectorAmpMultiplier(upgrades.CollectorAmp, prestigeCollectorMultiplier),
    criticalChance: calculateCriticalChance(upgrades.CriticalDischarge),
    criticalMultiplier: calculateCriticalMultiplier(upgrades.CriticalDischarge),
    offlineRewardRate: calculateOfflineRewardRate(upgrades.OfflineReactor),
    offlineMaxSeconds: clamp(21_600 + upgrades.OfflineReactor * 480 + Math.max(0, upgrades.OfflineReactor - 100) * 300, 21_600, 115_200),
    offlineRewardCapShare: clamp(0.1 + upgrades.OfflineReactor * 0.0018 + Math.max(0, upgrades.OfflineReactor - 60) * 0.0011, 0.1, 0.65),
    polarityEnabled: profileLevel >= 7 || upgrades.PolaritySync > 0 || upgrades.ShellLens > 0,
    polarityStackCap: clamp(3 + Math.floor(upgrades.PolaritySync / 8), 3, 12),
    polarityChargePerStack: clamp(0.055 + upgrades.PolaritySync * 0.0026 + Math.max(0, upgrades.PolaritySync - 40) * 0.0011, 0.055, 0.22),
    polarityChargeMax: progress.prestigeLevel > 0 ? 2.15 : 1.85,
    polarityPrestigeMultiplier: progress.prestigeLevel > 0 ? 1 + progress.prestigeLevel * 0.0025 : 1,
    polarityWindowCount: profileLevel >= 7 || upgrades.PolaritySync > 0 || upgrades.ShellLens > 0 ? clamp(1 + upgrades.GateCount + 1 + Math.floor(upgrades.ShellLens / 10), 2, 7) : 0,
    polarityWindowHalfDegrees: profileLevel >= 7 || upgrades.PolaritySync > 0 || upgrades.ShellLens > 0 ? clamp(8 + upgrades.ShellLens * 0.12, 8, 18) : 0,
    shellLensWindowBonus: Math.floor(upgrades.ShellLens / 10),
    shellLensLaneBonusMultiplier: upgrades.ShellLens >= 80 ? 1.12 : upgrades.ShellLens >= 40 ? 1.08 : 1
  };
}

function profileCoreValueMultiplier(level: number): number {
  const early = clamp(level - 1, 0, 9);
  const mid = Math.max(0, level - 10);
  const late = Math.max(0, level - 30);
  return 1 + early * 0.022 + early ** 1.8 * 0.003 + mid * 0.072 + mid ** 1.35 * 0.018 + late * 0.035 + late ** 1.25 * 0.012;
}

function profileChargeMultiplier(level: number): number {
  const early = clamp(level - 1, 0, 9);
  const mid = Math.max(0, level - 10);
  const late = Math.max(0, level - 30);
  return 1 + early * 0.024 + early ** 1.8 * 0.0026 + mid * 0.085 + mid ** 1.35 * 0.022 + late * 0.045 + late ** 1.25 * 0.015;
}

function calculateCoreBaseValue(level: number, profileLevel: number): number {
  const a = Math.max(0, level - 50);
  const b = Math.max(0, level - 30);
  const c = Math.max(0, level - 55);
  const d = Math.max(0, level - 60);
  const e = Math.max(0, level - 90);
  const base = 4 + level * 2 + level ** 2 * 1.25 + level ** 2.55 * 0.12;
  const ramp30 = 1 + b * 0.012 + b ** 1.27 * 0.005 + c * 0.024 + c ** 1.22 * 0.0065;
  const late50 = 1 + a * 0.01 + a ** 1.16 * 0.003;
  const ramp60a = 1 + d * 0.055 + d ** 1.28 * 0.024;
  const ramp60b = 1 + d * 0.032 + d ** 1.2 * 0.011 + e * 0.065 + e ** 1.18 * 0.024;
  return base * ramp30 * late50 * ramp60a * ramp60b * profileCoreValueMultiplier(profileLevel);
}

function calculateChargeValue(level: number, profileLevel: number): number {
  const a = Math.max(0, level - 50);
  const b = Math.max(0, level - 30);
  const c = Math.max(0, level - 55);
  const d = Math.max(0, level - 60);
  const e = Math.max(0, level - 90);
  const base = 0.8 + level * 0.82 + level ** 1.68 * 0.82 + level ** 2.24 * 0.055;
  const ramp30 = 1 + b * 0.03 + b ** 1.36 * 0.011 + c * 0.07 + c ** 1.28 * 0.018;
  const late50 = 1 + a * 0.024 + a ** 1.18 * 0.006;
  const ramp60a = 1 + d * 0.085 + d ** 1.3 * 0.03;
  const ramp60b = 1 + d * 0.04 + d ** 1.22 * 0.015 + e * 0.08 + e ** 1.18 * 0.028;
  return base * ramp30 * late50 * ramp60a * ramp60b * profileChargeMultiplier(profileLevel);
}

function calculateOverchargedChance(level: number, prestigeBonus: number): number {
  if (level === 0) {
    return prestigeBonus;
  }

  const x = Math.min(level, 30) / 30;
  const a = Math.max(0, level - 30);
  const b = Math.max(0, level - 80);
  const chance = 0.0025 + 0.0925 * x ** 1.18 + a * 0.00145 + a ** 1.12 * 0.00026 + b * 0.00075 + b ** 1.1 * 0.00014;
  return clamp(chance + prestigeBonus, 0, 0.36);
}

function calculateCollectorAmpMultiplier(level: number, prestigeMultiplier: number): number {
  const a = Math.max(0, level - 30);
  const b = Math.max(0, level - 60);
  const c = Math.max(0, level - 100);
  return (1 + level * 0.115 + level ** 1.25 * 0.0085 + a * 0.016 + a ** 1.18 * 0.0065 + b * 0.045 + b ** 1.16 * 0.018 + c * 0.08 + c ** 1.15 * 0.028) * prestigeMultiplier;
}

function calculateCriticalChance(level: number): number {
  if (level === 0) {
    return 0;
  }

  const a = Math.max(0, level - 60);
  const b = Math.max(0, level - 80);
  return clamp(0.028 + (level - 1) * 0.0042 + level ** 0.65 * 0.0012 + a * 0.0012 + a ** 1.08 * 0.0005 + b * 0.0018 + b ** 1.08 * 0.0009, 0, 0.68);
}

function calculateCriticalMultiplier(level: number): number {
  if (level === 0) {
    return 1;
  }

  const a = Math.max(0, level - 60);
  const b = Math.max(0, level - 80);
  return 1 + clamp(0.78 + level * 0.012 + level ** 0.65 * 0.006 + a * 0.006 + a ** 1.05 * 0.002 + b * 0.011 + b ** 1.05 * 0.004, 0, 4.8);
}

function calculateOfflineRewardRate(level: number): number {
  const a = Math.max(0, level - 10);
  const b = Math.max(0, level - 60);
  const c = Math.max(0, level - 100);
  return clamp(0.08 + level * 0.0038 + a * 0.0018 + b * 0.0018 + b ** 1.08 * 0.00045 + c * 0.0012, 0.08, 0.72);
}
